import { ReactNode } from "react";
import { ColumnDef } from "./types";

const SPREADSHEET_ACTIONS_FIELD = "_spreadsheetActions";

const DEFAULT_ACTIONS_COLUMN_WIDTH_PX = 200;

export interface SpreadsheetActionsColumnConfig {
    label: ReactNode;
    width?: number;
    /**
     * Pins the actions column to the right edge so data columns scroll under
     * it. Without this flag the column is pinned only when another column is
     * already pinned right (keeping the pinned group contiguous).
     */
    isPinnedRight?: boolean;
    cellFn: (props: {
        row: Record<string, any>;
        rowIdx: number;
        removeRow: (rowIdx: number) => void;
    }) => ReactNode;
}

export function isSpreadsheetActionsColumn(column: ColumnDef): boolean {
    return column.spreadsheetRole === "actions";
}

export function appendSpreadsheetActionsColumn(
    columns: ColumnDef[],
    actions: SpreadsheetActionsColumnConfig,
): ColumnDef[] {
    const pinActionsRight =
        actions.isPinnedRight ||
        columns.some((column) => column.pinned === "right");
    const actionsColumn: ColumnDef = {
        field: SPREADSHEET_ACTIONS_FIELD,
        label: actions.label,
        width: actions.width ?? DEFAULT_ACTIONS_COLUMN_WIDTH_PX,
        minSize: actions.width ?? DEFAULT_ACTIONS_COLUMN_WIDTH_PX,
        maxSize: actions.width ?? DEFAULT_ACTIONS_COLUMN_WIDTH_PX,
        isEditable: false,
        spreadsheetRole: "actions",
        pinned: pinActionsRight ? "right" : undefined,
        actionsCellFn: actions.cellFn,
    };
    return [...columns, actionsColumn];
}

export function getLastSelectableColumnIndex(columns: ColumnDef[]): number {
    for (let index = columns.length - 1; index >= 0; index -= 1) {
        if (!isSpreadsheetActionsColumn(columns[index])) {
            return index;
        }
    }
    return -1;
}

export function clampSelectableColumnIndex(
    columns: ColumnDef[],
    columnIndex: number,
): number {
    if (columnIndex < 0) {
        return 0;
    }
    const lastSelectable = getLastSelectableColumnIndex(columns);
    if (lastSelectable < 0) {
        return 0;
    }
    if (columnIndex > lastSelectable) {
        return lastSelectable;
    }
    if (
        columnIndex < columns.length &&
        isSpreadsheetActionsColumn(columns[columnIndex])
    ) {
        return Math.max(columnIndex - 1, 0);
    }
    return columnIndex;
}

export function findNextSelectableColumnIndex(
    columns: ColumnDef[],
    fromColumnIndex: number,
    step = 1,
): number | null {
    for (
        let columnIndex = fromColumnIndex + step;
        columnIndex >= 0 && columnIndex < columns.length;
        columnIndex += step
    ) {
        if (!isSpreadsheetActionsColumn(columns[columnIndex])) {
            return columnIndex;
        }
    }
    return null;
}
